import { useCallback, useEffect, useMemo } from 'react';
import useFetchComments from './useFetchComments';
import { useCollaborationSidebarActions } from '../store/collaborationSidebarProvider';
import { useRealtimeCommentingMessaging } from '../features/messaging/messageHandlers';
import useHasCommentingFeature from './useHasCommentingFeature';
import { trackInteraction } from '../utils/usageTracker';
export const useRealtimeCommentHandler = ({
  objectType,
  objectId,
  callback,
  skip
}) => {
  useRealtimeCommentingMessaging({
    skip,
    onReportNewComment: msg => {
      if (msg.payload.objectType !== objectType || msg.payload.objectId !== objectId) {
        return;
      }
      callback();
    }
  });
};
export default function useHasUnreadComments(objectType, objectId) {
  'use no memo';

  const {
    fetchComments,
    comments,
    updateCache: updateCommentsCache
  } = useFetchComments();
  const hasCommentingFeature = useHasCommentingFeature();
  const {
    updateUnreadReplies
  } = useCollaborationSidebarActions();
  const fetchCommentsCallback = useCallback(() => {
    fetchComments({
      objectType,
      objectId,
      refetch: true
    });
  }, [fetchComments, objectId, objectType]);
  useEffect(() => {
    fetchCommentsCallback();
  }, [fetchCommentsCallback]);
  useRealtimeCommentHandler({
    objectType,
    objectId,
    callback: () => {
      fetchCommentsCallback();
      trackInteraction({
        action: 'New comments while viewing asset'
      });
    },
    skip: !hasCommentingFeature
  });
  const unreadComments = useMemo(() => comments.filter(comment => !comment.read), [comments]);
  const unreadCommentsCount = useMemo(() => unreadComments.length, [unreadComments]);
  const hasUnreadComments = unreadCommentsCount > 0;
  const markCommentAsRead = useCallback(updatedCommentId => {
    if (!objectId || !objectType) {
      return;
    }
    updateCommentsCache({
      objectType,
      objectId,
      updatedCommentId
    });
  }, [objectId, objectType, updateCommentsCache]);
  useEffect(() => {
    updateUnreadReplies(unreadComments);
  }, [unreadComments, updateUnreadReplies]);
  return {
    hasUnreadComments,
    unreadCommentsCount,
    refetchComments: () => fetchComments({
      objectType,
      objectId,
      refetch: true
    }),
    markCommentAsRead
  };
}